import moment from "moment"

export const getOfferPricing = ({
    pricingTable,
    offerType,
    sessionsPerWeek,
    withCommitment,
}) =>
    Object.keys(pricingTable?.[offerType] ?? {})?.reduce(
        (acc, hoursPerMonth) => {
            const weeklyHours = sessionsPerWeek?.[hoursPerMonth]
            const pricingType = withCommitment ? "commitment" : "base"
            const hourlyPrice =
                pricingTable?.[offerType]?.[hoursPerMonth]?.[pricingType]

            return {
                ...acc,
                [weeklyHours]: { weeklyHours, hoursPerMonth, hourlyPrice },
            }
        },
        {}
    )

export const getOfferPrice = ({ offerPricing, hoursPerWeek }) => {
    const hoursPerMonth = offerPricing[hoursPerWeek].hoursPerMonth
    const hourlyPrice = offerPricing[hoursPerWeek].hourlyPrice
    const monthlyPrice = Math.round(hourlyPrice * hoursPerMonth * 10) / 10

    return { hourlyPrice, hoursPerMonth, monthlyPrice }
}

export const getDiscountDeadline = ({ user, startDate }) => {
    if (!user?.trial_ends_at) return null

    const trialEndsAt = moment(user.trial_ends_at).startOf("day")
    const isBeforeDeadline = moment().isBefore(trialEndsAt)

    if (!isBeforeDeadline) return null

    // Check if starting in current month
    const subscriptionStartsAt = moment(startDate)
    const isStartingInCurrentMonth = subscriptionStartsAt.isSame(
        moment(),
        "month"
    )

    if (!isStartingInCurrentMonth) return null

    return trialEndsAt.toISOString()
}

export const getDiscountAmount = ({
    amountToPay,
    isDiscountApplied,
    noDiscountPrice,
}) => {
    return isDiscountApplied && amountToPay
        ? Math.round(noDiscountPrice - amountToPay)
        : 0
}

/**
 * Faut-il annoncer la marche intermédiaire — le premier prélèvement, prorata du
 * mois entamé — entre le paiement du jour et la mensualité ?
 *
 * `SubscriptionCheckoutService::createCheckoutSession` produit TROIS formes de
 * lien, mais seulement DEUX montants du jour (vérifié en recette, contre la
 * lecture d'abord retenue) :
 *  - `deposit_only` (1 € forcé) et démarrage au mois suivant sans acompte
 *    encaissent tous deux 1 € : `computePayNowPrice` rend `depositPrice()` dès
 *    que le démarrage sort du mois courant. Le premier mois sera prélevé plus
 *    tard — sans cette ligne, la carte saute de l'acompte à la mensualité et la
 *    famille découvre le prorata sur son relevé ;
 *  - démarrage DANS le mois courant : le prorata est encaissé tout de suite,
 *    l'annoncer une seconde fois dirait deux fois la même chose.
 *
 * La question n'est donc ni le montant du jour — `amountToPay === 1` ne
 * reconnaissait l'acompte que tant que `depositPrice()` vaut 100 centimes — ni
 * la forme du lien, mais « le paiement du jour est-il déjà le premier mois ? ».
 * Comparaison arrondie des deux côtés : le prorata encaissé porte ses centimes
 * (66,11 €) là où l'échéance annoncée est un entier.
 *
 * `firstMonth` vient du serveur, calculé par la même fonction que le débit : rien
 * n'est recalculé ici.
 */
export const showsFirstMonthStep = ({ amountToPay, firstMonth }) =>
    !!firstMonth?.is_prorated &&
    Math.round(amountToPay ?? 0) !== Math.round(firstMonth.amount)

/**
 * Le délai au-delà duquel le formulaire de paiement Stripe est tenu pour absent.
 * Chez une famille dont le navigateur bloquait Stripe (Samsung Internet et son
 * bloqueur), la page tournait sans fin et la vente se perdait sans qu'aucune
 * erreur ne remonte : passé ce délai, la page propose de payer sur la page de
 * Stripe au lieu d'attendre.
 */
export const PAYMENT_ELEMENT_TIMEOUT_MS = 10000

/** Le temps laissé à « Confirmer et payer » avant de proposer le repli. */
export const PAYMENT_SLOW_MS = 45000

/**
 * Le verdict sur le formulaire de paiement Stripe monté dans notre page :
 * `loading` → `ready` quand Stripe le dit prêt, `load_error` quand Stripe dit
 * qu'il n'a pas pu le monter, `timeout` quand il ne dit rien à temps.
 *
 * L'erreur de chargement est définitive ; le délai dépassé ne l'est pas : un
 * formulaire lent qui finit par se monter redevient `ready`, et la page retire
 * son alerte.
 */
export const nextPaymentElementStatus = (current, event) => {
    if (event === "ready") return current === "load_error" ? current : "ready"
    if (event === "load_error")
        return current === "ready" ? current : "load_error"
    if (event === "timeout") return current === "loading" ? "timeout" : current
    return current
}

export const isPaymentElementFailed = (status) =>
    status === "timeout" || status === "load_error"

/** Le libellé du repli, partout où la page le propose. */
export const HOSTED_FALLBACK_LABEL = "Payer sur la page sécurisée Stripe"

/** Le message d'erreur d'un appel API du lien de vente, lisible par la famille. */
export const apiError = (
    err,
    fallback = "Une erreur inattendue s'est produite."
) =>
    err?.response?.data?.data?.error ?? err?.response?.data?.message ?? fallback
